import { useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Gift } from "lucide-react";

/**
 * Fires a one-time toast when a shop's welcome Premium trial has <= 24h left.
 * Stored per-trial-end timestamp in localStorage so we only nag once per trial.
 */
export function TrialEndingToast() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user || user.role !== "shop") return;
    const trialUntil = user.welcome_trial_until;
    const premiumUntil = user.premium_until;
    if (!trialUntil || !premiumUntil) return;
    const tier = user.subscription_tier || "free";
    const isPaid = tier === "pro" || tier === "premium";
    // Still on trial only if premium_until hasn't been extended past trial end.
    const stillOnTrial = new Date(premiumUntil).getTime() <= new Date(trialUntil).getTime() + 60_000;
    if (!stillOnTrial || !isPaid) return;

    const msLeft = new Date(trialUntil).getTime() - Date.now();
    const ONE_DAY = 86_400_000;
    if (msLeft <= 0 || msLeft > ONE_DAY) return;

    const storageKey = `mc-trial-ending-toast:${trialUntil}`;
    if (localStorage.getItem(storageKey)) return;

    const hoursLeft = Math.max(1, Math.round(msLeft / 3_600_000));
    const tierLabel = tier === "premium" ? "Premium" : "Pro";
    const tierPrice = tier === "premium" ? "$79.99/mo" : "$59.99/mo";
    toast(
      <div className="flex items-start gap-2" data-testid="trial-ending-toast">
        <Gift className="w-4 h-4 text-[#FF9500] mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold text-white text-sm">Your free trial ends in {hoursLeft}h</div>
          <div className="text-xs text-zinc-300 mt-0.5">Your {tierLabel} plan will auto-renew at {tierPrice}. Cancel any time.</div>
          <a
            href="/upgrade"
            className="inline-block mt-2 text-xs uppercase tracking-widest text-[#FF3B30] hover:underline"
            data-testid="trial-ending-toast-cta"
          >
            Manage plan →
          </a>
        </div>
      </div>,
      { duration: 12_000 }
    );
    localStorage.setItem(storageKey, String(Date.now()));
  }, [user]);

  return null;
}
